import * as z from "zod";
import { isoDate } from "./common";
import { SHOP_CAPABILITIES, STORE_ROLES } from "./store";

/**
 * The platform back-office's view of a shop — every store, claimed or not.
 *
 * Distinct from `Store` in `model/store.ts`, which is what a merchant sees of
 * their own shop. These rows carry the staff-only columns (`statusNote`,
 * `sourceEmail`) and the counts a moderation decision is made on.
 */

export const ADMIN_SHOP_SOURCES = ["MERCHANT", "IMPORTED"] as const;
export const ADMIN_SHOP_STATUSES = ["ACTIVE", "HIDDEN", "SUSPENDED"] as const;

export const SHOP_CLAIM_STATUSES = [
  "PENDING_DELIVERY",
  "ACTIVE",
  "REVOKED",
  "EXPIRED",
  "FAILED_DELIVERY",
  "REDEEMED",
] as const;
export const SHOP_CLAIM_DELIVERY_STATUSES = ["PENDING", "SENT", "FAILED"] as const;
export const SHOP_CLAIM_ORIGINS = ["ADMIN", "MOBILE"] as const;

export const AdminShopClaimInvitationAudit = z.object({
  event: z.string(),
  createdAt: isoDate,
  actorName: z.string().nullable(),
  note: z.string().nullable(),
});
export type AdminShopClaimInvitationAudit = z.output<typeof AdminShopClaimInvitationAudit>;

/** One invitation's lifecycle. Neither the token nor its digest is ever returned. */
export const AdminShopClaimInvitation = z.object({
  id: z.string(),
  status: z.enum(SHOP_CLAIM_STATUSES),
  origin: z.enum(SHOP_CLAIM_ORIGINS),
  claimEmail: z.string(),
  expiresAt: isoDate,
  createdAt: isoDate,
  sentAt: isoDate.nullable(),
  deliveryStatus: z.enum(SHOP_CLAIM_DELIVERY_STATUSES),
  error: z.string().nullable(),
  events: z.array(AdminShopClaimInvitationAudit),
});
export type AdminShopClaimInvitation = z.output<typeof AdminShopClaimInvitation>;

export const AdminShopClaimInvitations = z.object({
  /** Whether this shop can be invited to claim at all; `reason` says why not. */
  eligible: z.boolean(),
  reason: z.string().nullable(),
  claimEmail: z.string().nullable(),
  resendAvailableAt: isoDate.nullable(),
  canSend: z.boolean(),
  canRevoke: z.boolean(),
  invitations: z.array(AdminShopClaimInvitation),
});
export type AdminShopClaimInvitations = z.output<typeof AdminShopClaimInvitations>;

export const AdminShopPlace = z.object({
  city: z.string().nullable(),
  province: z.string().nullable(),
  country: z.string().nullable(),
});
export type AdminShopPlace = z.output<typeof AdminShopPlace>;

export const ADMIN_SHOP_CONNECTION_STATUSES = ["PENDING", "ACTIVE", "DISCONNECTED", "ERROR"] as const;

/** A commerce or payment provider the shop is linked to. */
export const AdminShopConnection = z.object({
  /** `shopify` or `stripe`. */
  provider: z.string(),
  status: z.enum(ADMIN_SHOP_CONNECTION_STATUSES),
  /** The `.myshopify.com` domain, or the Stripe account id. */
  account: z.string().nullable(),
});
export type AdminShopConnection = z.output<typeof AdminShopConnection>;

/** A row in the back-office table. */
export const AdminShop = z.object({
  id: z.string(),
  source: z.enum(ADMIN_SHOP_SOURCES),
  status: z.enum(ADMIN_SHOP_STATUSES),
  /** Why staff set the current status. Staff-only — never shown to a merchant or a customer. */
  statusNote: z.string().nullable(),
  statusChangedAt: isoDate.nullable(),
  name: z.string().nullable(),
  slug: z.string().nullable(),
  websiteUrl: z.string().nullable(),
  contactEmail: z.string().nullable(),
  /** Linked users with the Owner role. */
  ownerCount: z.number(),
  /** Every linked user. Zero means unclaimed — an imported row nobody has taken over. */
  memberCount: z.number(),
  locationCount: z.number(),
  connections: z.array(AdminShopConnection),
  /** What the shop has asked staff to switch on and is still waiting for, oldest first. */
  requestedCapabilities: z.array(z.enum(SHOP_CAPABILITIES)),
  place: AdminShopPlace.nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type AdminShop = z.output<typeof AdminShop>;

export const AdminShopList = z.object({
  shops: z.array(AdminShop),
  /** Rows matching the current filter — what pages the table. The rest are unfiltered. */
  total: z.number(),
  allShops: z.number(),
  imported: z.number(),
  claimed: z.number(),
  takenDown: z.number(),
  /** Waiting on staff for a capability. */
  requesting: z.number(),
});
export type AdminShopList = z.output<typeof AdminShopList>;

export const AdminShopOwner = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  role: z.enum(STORE_ROLES),
  linkedAt: isoDate,
});
export type AdminShopOwner = z.output<typeof AdminShopOwner>;

export const AdminShopLocation = z.object({
  id: z.string(),
  name: z.string(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string().nullable(),
  countryCode: z.string().nullable(),
  phone: z.string().nullable(),
  openingHours: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  isActive: z.boolean(),
});
export type AdminShopLocation = z.output<typeof AdminShopLocation>;

/** How much has actually happened at this shop — the context for a suspension. */
export const AdminShopActivity = z.object({
  customerCarts: z.number(),
  checkouts: z.number(),
  liveStreams: z.number(),
  draftOrders: z.number(),
  inventoryItems: z.number(),
  scans: z.number(),
});
export type AdminShopActivity = z.output<typeof AdminShopActivity>;

/** Whether this shop can be deleted, and the payment records that decide it. */
export const AdminShopDeletion = z.object({
  paidCheckouts: z.number(),
  paidBreakOrders: z.number(),
  /** Table orders money was paid for: this shop's at other shops' shows, and other shops' at the shows it hosts. */
  paidTableOrders: z.number(),
  /** True where the environment refuses the delete because the shop has taken money. Production only. */
  blocked: z.boolean(),
});
export type AdminShopDeletion = z.output<typeof AdminShopDeletion>;

/** What this shop may do. A shop never changed reports the defaults, with a null `updatedAt`. */
export const AdminShopCapabilities = z.object({
  /** "Went live" and "break on sale" pushed to every device, not only the shop's own buyers. */
  networkPush: z.boolean(),
  /** Hosts card-show events of its own and sells their tables. Off until staff switch it on. */
  hostEvents: z.boolean(),
  /** The counter: customer scans and carts, taking payment, the Shopify POS extension. On for a claimed shop. */
  pointOfSale: z.boolean(),
  /** Live streams, breaks and their shipping. */
  liveBreaks: z.boolean(),
  updatedAt: isoDate.nullable(),
});
export type AdminShopCapabilities = z.output<typeof AdminShopCapabilities>;

/** A shop's request for a capability, pending or turned down. A granted one is gone. */
export const AdminShopCapabilityRequest = z.object({
  capability: z.enum(SHOP_CAPABILITIES),
  status: z.enum(["PENDING", "DECLINED"]),
  /** Where the shop asked to be answered. */
  contactEmail: z.string().nullable(),
  createdAt: isoDate,
  declinedAt: isoDate.nullable(),
  /** Shown to the shop. */
  declineReason: z.string().nullable(),
});
export type AdminShopCapabilityRequest = z.output<typeof AdminShopCapabilityRequest>;

export const AdminShopDetail = AdminShop.extend({
  description: z.string().nullable(),
  logoUrl: z.string().nullable(),
  currencyCode: z.string().nullable(),
  returnsPolicy: z.string().nullable(),
  /** The directory's stable id for this row. Read-only: a re-import matches on it. */
  sourceRef: z.string().nullable(),
  /** The scraped claim address. Never published — this is the only route that returns it. */
  sourceEmail: z.string().nullable(),
  owners: z.array(AdminShopOwner),
  locations: z.array(AdminShopLocation),
  activity: AdminShopActivity,
  capabilities: AdminShopCapabilities,
  capabilityRequests: z.array(AdminShopCapabilityRequest),
  deletion: AdminShopDeletion,
});
export type AdminShopDetail = z.output<typeof AdminShopDetail>;

export const AdminShopDeletedOwner = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
});
export type AdminShopDeletedOwner = z.output<typeof AdminShopDeletedOwner>;

/** An owner the delete left in place, and why. */
export const AdminShopKeptOwner = AdminShopDeletedOwner.extend({
  reason: z.enum(["admin", "other-stores"]),
});
export type AdminShopKeptOwner = z.output<typeof AdminShopKeptOwner>;

export const AdminShopDeleted = z.object({
  id: z.string(),
  name: z.string().nullable(),
  /** Accounts removed with the shop, Firebase identity included. Empty unless `deleteOwners` was set. */
  ownersDeleted: z.array(AdminShopDeletedOwner),
  ownersKept: z.array(AdminShopKeptOwner),
});
export type AdminShopDeleted = z.output<typeof AdminShopDeleted>;

export interface AdminShopQuery {
  /** Matches name, slug, contact or source email, website, city, or an exact store id. */
  search?: string;
  source?: "all" | "MERCHANT" | "IMPORTED";
  claim?: "all" | "claimed" | "unclaimed";
  status?: "all" | "ACTIVE" | "HIDDEN" | "SUSPENDED";
  /** Only shops waiting on staff for a capability. */
  requested?: boolean;
  limit?: number;
  offset?: number;
}

/** Every field optional, and an empty string clears the column. */
export interface AdminShopPatch {
  name?: string;
  description?: string;
  logoUrl?: string;
  currencyCode?: string;
  websiteUrl?: string;
  slug?: string;
  contactEmail?: string;
  returnsPolicy?: string;
  sourceEmail?: string;
}

/** A field left out keeps its current value. */
export interface AdminShopCapabilitiesPatch {
  networkPush?: boolean;
  hostEvents?: boolean;
  pointOfSale?: boolean;
  liveBreaks?: boolean;
}

export interface AdminShopLocationPatch {
  name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
  countryCode?: string;
  phone?: string;
  openingHours?: string;
  /**
   * `null` clears the coordinate, where an empty string is what clears a text
   * column. `@IsOptional()` on the DTO skips `null` as well as `undefined`, so
   * it reaches the op, which stores it.
   */
  latitude?: number | null;
  longitude?: number | null;
  isActive?: boolean;
}
